import { Middleware, Store } from "redux";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import {
	ApplicationFactories,
	ComposeEnhancer,
	ModuleRegistryProvider
} from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { ApplicationModel } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { StoreFactories } from "@com.mgmtp.a12.client/client-core/lib/core/store";
import { DirtyHandlingFactories } from "@com.mgmtp.a12.client/client-core/lib/extensions/dirtyHandling";
import { createPlatformServerModelLoader } from "@com.mgmtp.a12.client/client-core/lib/extensions/modelLoader";
import { CRUDFactories } from "@com.mgmtp.a12.crud/crud-core";
import {
	createEmptyDocumentDataProvider,
	createFormEngineMiddlewares,
	formEngineDataReducers,
	formEngineSagas,
	FormModelProcessor
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { OverviewEngineFactories } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { TreeEngineFactories } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";
import { UaaActions, UaaClient, UaaMiddlewares, UaaReducer } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { workflowsMiddlewares } from "@com.mgmtp.a12.workflows/workflows-core/lib/middlewares";
import { workflowsSagas } from "@com.mgmtp.a12.workflows/workflows-core/lib/sagas";
import { LocaleActions } from "@com.mgmtp.a12.client/client-core/lib/core/locale";
import { Locale } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { integratedDocumentSagas } from "./app/integratedDocuments/sagas";
import { assignmentModalMiddlewares } from "./app/middlewares/assignmentModalMiddleware";
import { cancelActivityOnRemoveData } from "./app/middlewares/cancelActivityOnRemoveData";
import { documentTitleMiddleware } from "./app/middlewares/documentTitles";
import { logoutInAllTabsMiddleware } from "./app/middlewares/logoutInAllTabsMiddleware";
import { removeUnnecessaryNotificationMiddleware } from "./app/middlewares/removeUnnecessaryNotificationsMiddleware";
import { restoreOidcUserRolesMiddleware } from "./app/middlewares/restoreOidcUserRolesMiddleware";
import { handleEscapeSaga } from "./app/sagas/handleEscapeSaga";
import { loadAssigneeCandidatesSaga } from "./app/sagas/loadAssigneeCandidatesSaga";
import { loadEnvironmentDataSaga } from "./app/sagas/loadEnvironmentDataSaga";
import { LoadModelGraphSaga } from "./app/sagas/loadModelGraphSaga";
import { requiredFieldsSaga } from "./app/sagas/requiredFieldSaga";
import { datumValidationSaga } from "./app/sagas/datumValidationSaga";
import { datumValidationAFMSaga } from "./app/sagas/datumValidationAFMSaga";
import { modulFReducers } from "./app/slices/store";
import { customDataReducers } from "./app/store";
import { filterEmptyAttachmentsMiddleware } from "./attachment/filterEmptyAttachmentsMiddleware";
import { PortalAttachmentLoader } from "./attachment/modulfAttachmentLoader";
import * as appModel from "./base-appmodel.json";
import { overviewDocumentSagas } from "./component/auxiliaryOverviewEngine/sagas";
import { isProduction, uaaClientConfiguration } from "./config";
import { registerModulesOnLoginMiddleware, unregisterModulesOnLogoutMiddleware } from "./modules";
import { assignProcessSaga } from "./modules/assignment/assignProcessSaga";
import { assignStammdatenSaga } from "./modules/assignment/assignStammdatenSaga";
import { assignWiedervorlageSaga } from "./modules/assignment/assignWiedervorlageSaga";
import { createFocusSaga } from "./modules/focusHandling/sagas";
import { modulfPlatformSingleDocumentDataProvider } from "./providers/modulfPlatformSingleDocumentDataProvider";
import { textbausteinTreeDataProvider } from "./providers/textbausteine/textbausteinTreeDataProvider";
import { transientDataProvider } from "./providers/transientDataProvider";
import { assertNonNullable } from "./utilities/assert";
import { overviewEngineDataLoader } from "./app/provider/overviewEngineDataLoader";

/**
 * Enable A12 Client Chrome Extension support
 */
const devToolMiddleware = (): Middleware => {
	return window._sampleDevToolMiddleware === undefined
		? (window._sampleDevToolMiddleware = StoreFactories.createMiddleware((api, next, action) => {
				return next(action);
			}))
		: window._sampleDevToolMiddleware;
};

export interface StoreHolder {
	store?: Store;
}

const storeHolder: StoreHolder = {};

const config = {
	model: appModel as ApplicationModel,
	modelLoader: createPlatformServerModelLoader({ modelProcessors: [FormModelProcessor] }),
	dataHandlers: [
		textbausteinTreeDataProvider,
		createEmptyDocumentDataProvider(),
		transientDataProvider,
		modulfPlatformSingleDocumentDataProvider,
		TreeEngineFactories.createDataProvider(),
		...OverviewEngineFactories.createDataProviders(overviewEngineDataLoader),
		...OverviewEngineFactories.createDataProviders()
	],
	dataReducers: [
		...customDataReducers,
		...formEngineDataReducers,
		...TreeEngineFactories.createDataReducers(),
		...OverviewEngineFactories.createDataReducers()
	],
	additionalMiddlewares: [
		logoutInAllTabsMiddleware,
		restoreOidcUserRolesMiddleware,
		filterEmptyAttachmentsMiddleware,
		...UaaMiddlewares(),
		...createFormEngineMiddlewares(),
		CRUDFactories.createCRUDMiddleware(),
		...OverviewEngineFactories.createMiddlewares(),
		...workflowsMiddlewares(undefined, {
			includeAdaptAppModelMiddleware: false,
			includeAddFormKeyToPushAction: false,
			includeAddFormKeyToReplacementAction: false,
			includeReloadTaskOverviewAfterAssignTaskMiddleware: false,
			includeReloadTaskOverviewAfterCompleteTaskMiddleware: false,
			includeReloadTaskOverviewAfterSendMessageMiddleware: false,
			includeReloadTaskOverviewAfterStartProcessMiddleware: false
		}),
		registerModulesOnLoginMiddleware,
		unregisterModulesOnLogoutMiddleware,
		devToolMiddleware(),
		documentTitleMiddleware,
		cancelActivityOnRemoveData,
		removeUnnecessaryNotificationMiddleware,
		...assignmentModalMiddlewares
	],
	overridePlatformSagas: [
		...DirtyHandlingFactories.createSagas(),
		...workflowsSagas,
		...OverviewEngineFactories.createApplicationSagas()
	],
	customSagas: [
		...CRUDFactories.createSagas(),
		...createFocusSaga(),
		...formEngineSagas({ attachmentLoader: new PortalAttachmentLoader() }),
		LoadModelGraphSaga,
		handleEscapeSaga,
		loadEnvironmentDataSaga,
		requiredFieldsSaga,
		datumValidationSaga,
		datumValidationAFMSaga,
		assignProcessSaga,
		assignStammdatenSaga,
		assignWiedervorlageSaga,
		loadAssigneeCandidatesSaga,
		...overviewDocumentSagas,
		...integratedDocumentSagas
	],
	applicationBusyTriggers: {
		start: [UaaActions.loggingInOIDC, UaaActions.loggingOut],
		end: [UaaActions.loggedIn, UaaActions.loginFailed, UaaActions.loggedOut, UaaActions.logoutFailed]
	},
	applicationResetTriggers: {
		resetRequested: [UaaActions.logoutRequested],
		resetConfirmed: UaaActions.loggingOut(),
		reset: [UaaActions.loggedOut]
	},
	preComputeNewDocuments: true,
	locale: Locale.fromString("de_DE") as Locale,
	reducerMap: { uaa: UaaReducer, ...modulFReducers },
	composeEnhancer: isProduction ? undefined : enableReduxDevTools()
};

export function createApplicationStore(): Promise<Store> {
	ModuleRegistryProvider.getInstance().addModule(TreeEngineFactories.createModule());

	const store = ApplicationFactories.createApplicationSetup(config).store;
	storeHolder.store = store;

	/*
	 * Überschreibung des window.onbeforeunload Handlers, um einen Bestätigungsdialog anzuzeigen, wenn die Seite
	 * geschlossen wird und dirty oder locked Activities existieren.
	 */
	window.onbeforeunload = () => {
		// TODO: Anders implementieren, falls sich eine sauberere Methode ohne window Property ergibt
		// Bevor die aktuelle Seite geschlossen wird, entscheidet das suppressClosePrompt-Flag darüber, ob der User
		// das Verlassen der Seite bestätigen muss. Falls das Flag auf true gesetzt ist, wird es nach einmaliger
		// Anwendung wieder auf false gesetzt, da die Bestätigung standardmäßig aktiviert sein soll.
		if (window.suppressClosePrompt) {
			window.suppressClosePrompt = false;
			return undefined;
		}

		// Dialog anzeigen, wenn dirty oder locked Activities existieren.
		const dirtySubTree = ActivitySelectors.allDirtyOrLockedActivities()(store.getState());
		if (dirtySubTree.length > 0) {
			// Wird in den meisten modernen Browserversionen nicht angezeigt, stattdessen werden browserspezifische Nachrichten
			// verwendet (https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#Browser_compatibility)
			return "Von Ihnen vorgenommene Änderungen werden möglicherweise nicht gespeichert.";
		}

		return undefined;
	};

	store.dispatch(LocaleActions.set(Locale.fromString("de_DE") as Locale));

	return initializeUAAClient(store);
}

const BEFORE_LOGIN_URL_KEY = "beforeLoginUrl";

async function initializeUAAClient(store: Store): Promise<Store> {
	await UaaClient.init({ ...uaaClientConfiguration, store });

	const client = UaaClient.getOidcClient();
	client.initConnector();

	const config = UaaClient.getOidcConfiguration();
	assertNonNullable(config.redirect_uri);
	assertNonNullable(config.post_logout_redirect_uri);

	switch (window.location.pathname) {
		case getPathname(config.redirect_uri):
			return client
				.processLoginCallback()
				.then(() => {
					const beforeLoginUrl = sessionStorage.getItem(BEFORE_LOGIN_URL_KEY);
					window.history.replaceState(null, document.title, beforeLoginUrl ?? "/");
					sessionStorage.removeItem(BEFORE_LOGIN_URL_KEY);
					return store;
				})
				.catch(error => {
					window.location.assign("/");
					return Promise.reject(error);
				});
		case getPathname(config.post_logout_redirect_uri):
			sessionStorage.removeItem(BEFORE_LOGIN_URL_KEY);
			return client.processLogoutCallback().then(() => store);
		default:
			sessionStorage.setItem(BEFORE_LOGIN_URL_KEY, window.location.href.substring(window.location.origin.length));
			client.login();
			return store;
	}
}

function getPathname(value: string): string {
	return new URL(value, window.location.origin).pathname;
}

/**
 * Enable devtools
 */
declare let window: Window & {
	__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: ComposeEnhancer;
	_sampleDevToolMiddleware?: Middleware;
};

/**
 * Enable Redux DevTools
 */
function enableReduxDevTools(): ComposeEnhancer | undefined {
	return typeof window !== "undefined" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ !== undefined
		? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
		: undefined;
}
